import {
  DataRecord,
  DataSequence,
  DataValue,
  DatumWrapper,
  JsonArray,
  JsonDataWrapper,
  JsonObject,
  JsonSerializableObject,
  JsonValue,
} from '@suntex.core/types';
import { isArray, isIterable } from '@suntex.core/utils';

import { UnknownLogDataProvider } from './types';

// --- Configuration ---

export const DEFAULT_MAX_DEPTH = 10;
export const CYCLIC_FLAG_KEY = '!cyclic';
export const TRUNCATION_FLAG_KEY = '!truncated';
export const TYPE_TOKEN_KEY = '$type';

export type Evaluateable = UnknownLogDataProvider | DataValue | undefined;

export interface EvaluateOptions {
  maxDepth?: number;
}

export interface SerializeOptions {
  maxDepth?: number;
  omitNull?: boolean;
}

// --- Public API ---

export function evaluate(
  src: Evaluateable,
  opts: EvaluateOptions = {},
): DataValue | undefined {
  const resolved = typeof src === 'function' ? src() : src;
  if (resolved === undefined) return undefined;
  return src === resolved
    ? (resolved as DataValue)
    : sanitizeUnknownDataValue(
        resolved,
        new Set(),
        opts.maxDepth ?? DEFAULT_MAX_DEPTH,
      );
}

export function serialize(
  src: Evaluateable,
  opts: SerializeOptions = {},
): JsonObject | undefined {
  const v = serializeDataValue(evaluate(src, opts), {
    ...opts,
    depth: 0,
    maxDepth: opts.maxDepth ?? DEFAULT_MAX_DEPTH,
  });
  if (v === undefined) return undefined;
  return v && typeof v === 'object' && !isArray(v) ? v : { $: v };
}

// --- Type Guards ---

const PRIMITIVE_TYPES = new Set(['string', 'number', 'boolean', 'bigint']);

function isDatumWrapper(value: object): value is DatumWrapper {
  return (
    typeof value.valueOf === 'function' &&
    PRIMITIVE_TYPES.has(typeof value.valueOf())
  );
}

function isJsonDataWrapper(value: DataRecord): value is JsonDataWrapper {
  return typeof (value as JsonDataWrapper)['$'] === 'string';
}

function isJsonSerializable(value: object): value is JsonSerializableObject {
  return typeof (value as JsonSerializableObject)['toJSON'] === 'function';
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

// --- Helpers ---

function buildRecord<T>(
  source: Readonly<object>,
  transform: (val: unknown) => T | undefined,
): Record<string, T> | undefined {
  const record: Record<string, T> = {};
  let hasProps = false;

  for (const key in source) {
    const value = transform((source as Record<string, unknown>)[key]);
    if (value !== undefined) {
      record[key] = value;
      hasProps = true;
    }
  }

  return hasProps ? record : undefined;
}

function sanitizeUnknownDataValue(
  value: unknown,
  known: Set<unknown>,
  depth: number,
): DataValue {
  try {
    if (value === null) return null;
    if (value === undefined) return undefined;
    if (isPrimitive(value) || typeof value === 'bigint') return value;
    if (typeof value !== 'object') return undefined;

    // Handle cyclic references
    if (known.has(value)) return { [CYCLIC_FLAG_KEY]: true };

    // Handle max depth
    if (depth <= 0) return { [TRUNCATION_FLAG_KEY]: true };
    known.add(value);

    const nextDepth = depth - 1;

    // Preserve special wrappers as-is
    if (isJsonSerializable(value) || isDatumWrapper(value)) return value;

    // Handle iterables and plain objects
    if (isIterable(value)) {
      return Array.from(value as DataSequence).map((item) =>
        sanitizeUnknownDataValue(item, known, nextDepth),
      );
    }

    return buildRecord(value, (v) =>
      sanitizeUnknownDataValue(v, known, nextDepth),
    );
  } catch (e) {
    return { $error: sanitizeUnknownDataValue(e, known, depth) };
  }
}

type InternalSerializeOpts = {
  omitNull?: boolean;
  depth: number;
  maxDepth: number;
};

function serializeDataValue(
  value: DataValue,
  opts: InternalSerializeOpts = { depth: 0, maxDepth: DEFAULT_MAX_DEPTH },
): JsonValue | undefined {
  if (value === null) return opts.omitNull ? undefined : null;
  if (value === undefined) return undefined;
  if (isPrimitive(value)) return value;
  if (typeof value === 'bigint')
    return wrapJsonValue(serializeBigInt(value), 'bigint');
  if (typeof value !== 'object') return undefined;

  // Handle max depth
  if (opts.depth >= opts.maxDepth) {
    return { $: null, [TRUNCATION_FLAG_KEY]: true };
  }

  const nextOpts = { ...opts, depth: opts.depth + 1 };
  const $type = getTypeToken(value);

  if (isJsonSerializable(value)) {
    return wrapJsonValue(value.toJSON(), $type);
  }

  if (isDatumWrapper(value)) {
    // Built-in wrappers unwrap directly
    if (
      value instanceof String ||
      value instanceof Number ||
      value instanceof Boolean
    ) {
      return value.valueOf();
    }
    const primitiveValue = value.valueOf();
    return wrapJsonValue(
      typeof primitiveValue === 'bigint'
        ? serializeBigInt(primitiveValue)
        : primitiveValue,
      $type,
    );
  }

  if (isIterable(value)) {
    const arr: JsonArray = Array.from(value as DataSequence).map((item) => {
      const v = serializeDataValue(item, nextOpts);
      return v === undefined ? null : v;
    });
    return isArray(value) ? arr : wrapJsonValue(arr, $type);
  }

  // Plain object / DataRecord
  if (isJsonDataWrapper(value)) return { ...value };

  return buildRecord(value, (v) =>
    serializeDataValue(v as DataValue, nextOpts),
  );
}

function serializeBigInt(value: bigint): number | string {
  return value >= Number.MIN_SAFE_INTEGER && value <= Number.MAX_SAFE_INTEGER
    ? Number(value)
    : value.toString();
}

function wrapJsonValue<T extends JsonValue>(
  value: T,
  type?: string,
): JsonObject {
  return type ? { $: value, [TYPE_TOKEN_KEY]: type } : { $: value };
}

function getTypeToken(value: object): string {
  const name = value.constructor.name;
  return name !== 'Object' ? name : 'object';
}
